import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, TrendingUp, Award, Truck, AlertTriangle, Calendar, ArrowLeft,
} from 'lucide-react';
import NECSLogo from '../components/NECSLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PropertyTrustPage() {
  const { propertyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/property/${propertyId}/trust`);
        if (res.status === 404) { setNotFound(true); }
        else if (res.ok) { setData(await res.json()); }
      } catch (_e) {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [propertyId]);

  if (loading) {
    return (
      <Centered>
        <p className="text-zinc-400">Loading property report…</p>
      </Centered>
    );
  }

  if (notFound) {
    return (
      <Centered>
        <Card className="bg-zinc-900 border border-red-500/40 p-8 text-center max-w-md" data-testid="trust-not-found">
          <h1 className="text-2xl font-black text-red-400 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            PROPERTY NOT FOUND
          </h1>
          <p className="text-sm text-zinc-400">No public report exists for this property ID.</p>
        </Card>
      </Centered>
    );
  }

  const m = data.metrics;
  const since = data.property.since ? new Date(data.property.since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : '—';

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <NECSLogo variant="default" size="md" align="center" />
        </div>

        <Card
          className="p-8 mb-5"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(201,169,98,0.18), transparent 70%), #1a1a1a',
            border: '2px solid #C9A962',
          }}
          data-testid="trust-header"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <ShieldCheck className="h-8 w-8 text-[#C9A962]" />
            <h1
              className="text-3xl font-black tracking-tight"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              SERVICE TRUST REPORT
            </h1>
          </div>
          <p className="text-center text-2xl font-bold text-white" data-testid="trust-property-name">
            {data.property.property_name}
          </p>
          {data.property.address && (
            <p className="text-center text-sm text-zinc-400 mt-1">{data.property.address}</p>
          )}
          <p className="text-center text-[11px] uppercase tracking-widest text-zinc-500 mt-3">
            Serviced by NECS since {since}
          </p>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5" data-testid="trust-metrics">
          <Metric icon={<Calendar className="h-5 w-5" />} label="Service uptime (30d)"
                  value={m.uptime_pct_30d != null ? `${m.uptime_pct_30d}%` : '—'}
                  caption={`${m.served_days_30d} of 30 days served`} />
          <Metric icon={<TrendingUp className="h-5 w-5" />} label="On-time pickups"
                  value={m.on_time_pickup_pct_30d != null ? `${m.on_time_pickup_pct_30d}%` : '—'}
                  caption={`${m.completed_30d} completed · ${m.missed_30d} missed`} />
          <Metric icon={<Award className="h-5 w-5" />} label="Certified staff"
                  value={m.certified_pct_30d != null ? `${m.certified_pct_30d}%` : '—'}
                  caption={`${m.certified_staff_30d} of ${m.active_staff_30d} on-site`} />
          <Metric icon={<Truck className="h-5 w-5" />} label="Pickups (7d)"
                  value={m.pickups_7d}
                  caption={`${m.pickups_30d} in last 30d`} />
          <Metric icon={<AlertTriangle className="h-5 w-5" />} label="Violations (30d)"
                  value={m.violations_30d}
                  caption={`${m.violations_resolved_30d} resolved`} />
          <Metric icon={<ShieldCheck className="h-5 w-5" />} label="Service quality"
                  value={qualityBadge(m)}
                  caption="Auto-graded from above" />
        </div>

        <Card className="bg-zinc-900 border border-white/10 p-5 text-center" data-testid="trust-footer">
          <p className="text-xs text-zinc-400">
            All metrics are computed from real QR-scan pickup data, violation reports, and certified-staff records — no manual input.
          </p>
          <p className="text-[11px] text-zinc-600 mt-2">
            Issued by {data.issued_by} · As of {new Date(data.as_of).toLocaleString()}
          </p>
        </Card>

        <div className="text-center mt-6">
          <Button
            variant="outline"
            className="border-[#C9A962] text-[#C9A962]"
            onClick={() => { window.location.href = '/'; }}
            data-testid="trust-home"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to home
          </Button>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white flex items-center justify-center px-4">
      {children}
    </div>
  );
}

function Metric({ icon, label, value, caption }) {
  return (
    <Card className="bg-zinc-900 border border-white/10 p-4" data-testid={`trust-metric-${slug(label)}`}>
      <div className="flex items-center gap-2 text-[#C9A962] mb-2">
        {icon}
        <span className="text-[11px] uppercase tracking-widest text-zinc-400">{label}</span>
      </div>
      <p className="text-3xl font-black text-white leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {value}
      </p>
      {caption && <p className="text-[11px] text-zinc-500 mt-2">{caption}</p>}
    </Card>
  );
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function qualityBadge(m) {
  const score = (m.uptime_pct_30d || 0) * 0.5 + (m.on_time_pickup_pct_30d || 0) * 0.5;
  if (score >= 90) return 'A+';
  if (score >= 75) return 'A';
  if (score >= 60) return 'B';
  if (score >= 40) return 'C';
  return '—';
}
